import { Facebook, Phone, Clock } from "lucide-react";
import { waLink, WA_DEFAULT_MSG, WA_NUMBER_DISPLAY, PHONE_LINK } from "@/lib/wa";
import { STR } from "@/data/i18n";
import { useLang } from "@/lib/lang";
import { WhatsAppIcon, ThreadsIcon } from "./Shared";

export const Footer = () => {
    const { lang } = useLang();
    const t = STR.footer[lang];
    const nav = STR.nav[lang].links;

    return (
        <footer data-testid="footer" className="border-t border-white/10 bg-[#070707]">
            <div className="mx-auto max-w-7xl px-5 py-16 md:px-8">
                <div className="grid grid-cols-1 gap-12 md:grid-cols-3">
                    <div>
                        <img
                            src="/assets/techflaze-logo.webp"
                            alt="Logo TechFlaze"
                            className="h-11 w-auto"
                            width="160"
                            height="44"
                        />
                        <p className="mt-5 max-w-xs text-sm leading-relaxed text-zinc-400">
                            {t.tagline}
                        </p>
                    </div>
                    <div>
                        <h3 className="text-sm font-semibold uppercase tracking-widest text-slate-200">
                            {t.contact}
                        </h3>
                        <ul className="mt-5 space-y-3 text-sm text-zinc-400">
                            <li>
                                <a
                                    href={waLink(WA_DEFAULT_MSG)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="footer-wa-link"
                                    className="inline-flex items-center gap-2 transition-colors duration-200 hover:text-amber-400"
                                >
                                    <WhatsAppIcon className="h-4 w-4" />
                                    WhatsApp: {WA_NUMBER_DISPLAY}
                                </a>
                            </li>
                            <li>
                                <a
                                    href={PHONE_LINK}
                                    data-testid="footer-phone-link"
                                    className="inline-flex items-center gap-2 transition-colors duration-200 hover:text-amber-400"
                                >
                                    <Phone className="h-4 w-4" />
                                    {STR.misc[lang].call}: {WA_NUMBER_DISPLAY}
                                </a>
                            </li>
                            <li data-testid="footer-hours" className="inline-flex items-center gap-2">
                                <Clock className="h-4 w-4" />
                                {t.hours}
                            </li>
                        </ul>
                        <div className="mt-6 flex items-center gap-3">
                            <a
                                href="https://www.facebook.com/TechFlaze"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Facebook TechFlaze"
                                data-testid="footer-facebook-link"
                                className="flex h-11 w-11 items-center justify-center rounded-full border border-white/10 text-zinc-400 transition-colors duration-200 hover:border-amber-400/50 hover:text-amber-400"
                            >
                                <Facebook className="h-5 w-5" />
                            </a>
                            <a
                                href="https://www.threads.net/@techflaze"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Threads TechFlaze"
                                data-testid="footer-threads-link"
                                className="flex h-11 w-11 items-center justify-center rounded-full border border-white/10 text-zinc-400 transition-colors duration-200 hover:border-amber-400/50 hover:text-amber-400"
                            >
                                <ThreadsIcon className="h-5 w-5" />
                            </a>
                        </div>
                    </div>
                    <div>
                        <h3 className="text-sm font-semibold uppercase tracking-widest text-slate-200">
                            {t.nav}
                        </h3>
                        <ul className="mt-5 space-y-3 text-sm text-zinc-400">
                            {nav.map((l) => (
                                <li key={l.id}>
                                    <a
                                        href={l.href}
                                        data-testid={`footer-link-${l.id}`}
                                        className="transition-colors duration-200 hover:text-amber-400"
                                    >
                                        {l.label}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
                <div className="mt-14 flex flex-col items-center justify-between gap-3 border-t border-white/5 pt-8 text-center text-xs text-zinc-500 md:flex-row md:text-left">
                    <p data-testid="footer-copyright">{t.rights}</p>
                    <p>{t.founded}</p>
                </div>
            </div>
        </footer>
    );
};
